/**
 * The Complete step's flow (manual-booking-redesign D9; shared-client-screens D3, web-account-pages
 * D11), one home for the Mini App and the studio website: it asks the server for the next step on
 * entry and after every submit (`POST /booking-hub/booking/:id/complete`, then
 * `POST /booking-hub/booking/:id/forms/:formId`): registration first for a visitor, then each
 * outstanding form, then the payment, then done or the studio's review. Each form sends in one go.
 *
 * Each side passes its transport (`fetcher`, which throws `{ status, code, details }` on a failed
 * answer) and where each ending goes: the app's pay door and end screen, or the website's pay and
 * booked pages.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CompleteStepFormSubmitResponse, CompleteStepResponse } from '@telify/shared';
import type { FieldError, FormResponseInput } from '@/types/forms';

export type CompleteStep = Extract<CompleteStepResponse['next'], { form: unknown }>;

/** A failed answer, as each side's fetcher throws it. */
export interface CompleteStepError {
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
}

export type CompleteFetcher = <T>(path: string, init?: RequestInit) => Promise<T>;

export interface UseCompleteStepInput {
  bookingId: string;
  /** False until the side's session is ready. */
  enabled: boolean;
  fetcher: CompleteFetcher;
  /** Only payment is left. */
  onPay: () => void;
  /** Everything is in: the booking is booked. */
  onDone: () => void;
  /** The studio reviews the forms first, or the booking no longer waits: its page says where it stands. */
  onPendingApproval: () => void;
}

export type CompleteStepState = 'loading' | 'form' | 'submitting' | 'error';

export interface CompleteStepFlow {
  state: CompleteStepState;
  step: CompleteStep | null;
  completeBy: string | null;
  serverErrors: FieldError[];
  /** A visitor's first registration needs the ticked privacy box (registration-privacy-consent). */
  privacy: { required: boolean; ticked: boolean; error: boolean; onChange: (checked: boolean) => void };
  submit: (responses: FormResponseInput[]) => void;
  retry: () => void;
}

/** The booking is not (or no longer) waiting, or is not this client's: its page tells the client where it stands. */
const LEAVE_CODES = new Set(['BOOKING_NOT_FOUND', 'NOT_WAITING', 'COMPLETION_EXPIRED']);
/** The step the page showed is stale: ask the server for the current one. */
const REFRESH_CODES = new Set(['FORM_NOT_OUTSTANDING', 'REGISTRATION_FIRST', 'FORM_NOT_FOUND']);

export function useCompleteStep({ bookingId, enabled, fetcher, onPay, onDone, onPendingApproval }: UseCompleteStepInput): CompleteStepFlow {
  const [step, setStep] = useState<CompleteStep | null>(null);
  const [completeBy, setCompleteBy] = useState<string | null>(null);
  const [state, setState] = useState<CompleteStepState>('loading');
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);
  const [privacyTicked, setPrivacyTicked] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);
  const privacyRequired = step?.step === 'registration' && step.form.privacyConsentRequired === true;

  // The endings are the sides' navigations; read the latest without re-running the entry load.
  const endings = useRef({ onPay, onDone, onPendingApproval });
  endings.current = { onPay, onDone, onPendingApproval };

  /** Show the next step, or hand off: the payment, done, or the booking page. */
  const apply = useCallback((response: CompleteStepResponse) => {
    setCompleteBy(response.completeBy);
    const next = response.next;
    if (next.step === 'pay') return endings.current.onPay();
    // Finished: the same end screen self-booking ends on (Gabriel 2026-10-08). Waiting on the
    // studio's approval is not an ending, so that shows on the booking page.
    if (next.step === 'done') return endings.current.onDone();
    if (next.step === 'pending_approval') return endings.current.onPendingApproval();
    setStep(next);
    setServerErrors([]);
    setState('form');
  }, []);

  const fail = useCallback((err: CompleteStepError) => {
    if (err.code && LEAVE_CODES.has(err.code)) {
      endings.current.onPendingApproval();
      return;
    }
    setState('error');
  }, []);

  const load = useCallback(async () => {
    setState('loading');
    try {
      apply(await fetcher<CompleteStepResponse>(`/booking-hub/booking/${bookingId}/complete`, { method: 'POST' }));
    } catch (err) {
      fail(err as CompleteStepError);
    }
  }, [apply, bookingId, fail, fetcher]);

  // Advance on entry: a booking whose work is already done moves on at once (D9).
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (enabled) void loadRef.current();
  }, [bookingId, enabled]);

  const submit = useCallback(
    async (responses: FormResponseInput[]) => {
      if (!step) return;
      if (privacyRequired && !privacyTicked) {
        setPrivacyError(true);
        return;
      }
      setState('submitting');
      setServerErrors([]);
      try {
        apply(await fetcher<CompleteStepFormSubmitResponse>(`/booking-hub/booking/${bookingId}/forms/${step.form.formId}`, {
          method: 'POST',
          body: JSON.stringify({ responses, ...(privacyRequired ? { privacyAccepted: true } : {}) }),
        }));
      } catch (err) {
        const e = err as CompleteStepError;
        const fieldErrors = e.details?.fieldErrors;
        if (e.status === 400 && Array.isArray(fieldErrors)) {
          setServerErrors(fieldErrors as FieldError[]);
          setState('form');
          return;
        }
        if (e.code && REFRESH_CODES.has(e.code)) {
          void load();
          return;
        }
        fail(e);
      }
    },
    [apply, bookingId, fail, fetcher, load, step, privacyRequired, privacyTicked],
  );

  return {
    state,
    step,
    completeBy,
    serverErrors,
    privacy: {
      required: privacyRequired,
      ticked: privacyTicked,
      error: privacyError,
      onChange: (checked) => {
        setPrivacyTicked(checked);
        if (checked) setPrivacyError(false);
      },
    },
    submit: (responses) => void submit(responses),
    retry: () => void load(),
  };
}
